// AppShell — Layout principal avec sidebar navigation
// Phase 2 P6 (fix bug) : menu latéral complet vers tous les modules Sprint 1-5
// (Dashboard, Maintenance, Engins, Stocks, DA, Agenda, Stats, Admin)
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { DatabaseBackup,LayoutDashboard,
  LogOut,
  Wrench,
  Moon,
  Sun,
  Truck,
  Package,
  ShoppingCart,
  CalendarDays,
  BarChart3,
  Shield,
  Users,
  Upload,
  FileText,
  MapPin,
  Building2, Menu, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useCan } from "@/hooks/useCan";
import { Button } from "@/components/ui/button";
import { useT, getLocale, setLocale, type Locale } from "@/lib/i18n";
import { useEffect, useState } from "react";
import { ht } from "@/lib/ht";

interface NavItem {
  to: string;
  labelKey: string; // P24 — i18n key
  icon: React.ComponentType<{ className?: string }>;
  respoOnly?: boolean; // Si true, affiché uniquement pour respo+
}

/**
 * P57-B — Menu regroupé selon la suite logique du process de maintenance.
 *
 * L'ordre des groupes n'est pas cosmétique : il suit le process réel.
 *   Pilotage      → où en est-on ?
 *   Exploitation  → le cœur : intervenir sur les engins
 *   Logistique    → approvisionner (DA, stock, fournisseurs)
 *   Référentiel   → le socle : de quoi a-t-on besoin pour intervenir
 *   Administration→ outillage (utilisateurs, audit, import, sauvegardes)
 *
 * Avant, « Sites » et « Fournisseurs » apparaissaient APRÈS « Sauvegardes »,
 * ce qui suggérait à tort qu'ils relevaient de l'administration.
 */
const NAV_GROUPS: { groupKey: string; items: NavItem[] }[] = [
  {
    groupKey: "nav.group_pilotage",
    items: [
      { to: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard },
      { to: "/responsable/statistiques", labelKey: "nav.statistiques", icon: BarChart3, respoOnly: true },
    ],
  },
  {
    groupKey: "nav.group_exploitation",
    items: [
      { to: "/maintenance", labelKey: "nav.interventions", icon: Wrench },
      { to: "/agenda-maintenance", labelKey: "nav.agenda", icon: CalendarDays },
      { to: "/engins", labelKey: "nav.engins", icon: Truck },
    ],
  },
  {
    groupKey: "nav.group_logistique",
    items: [
      { to: "/demandes-achat", labelKey: "nav.demandes_achat", icon: ShoppingCart },
      { to: "/stocks", labelKey: "nav.stocks", icon: Package },
      { to: "/fournisseurs", labelKey: "nav.fournisseurs", icon: Building2, respoOnly: true },
    ],
  },
  {
    groupKey: "nav.group_referentiel",
    items: [
      { to: "/sites", labelKey: "nav.sites", icon: MapPin, respoOnly: true },
      { to: "/contrats", labelKey: "nav.contrats", icon: FileText, respoOnly: true },
    ],
  },
  {
    groupKey: "nav.group_administration",
    items: [
      { to: "/admin/users", labelKey: "nav.users", icon: Users, respoOnly: true },
      { to: "/admin/audit-logs", labelKey: "nav.audit", icon: Shield, respoOnly: true },
      { to: "/admin/import", labelKey: "nav.import", icon: Upload, respoOnly: true },
      { to: "/admin/backups", labelKey: "nav.backups", icon: DatabaseBackup, respoOnly: true },
    ],
  },
];


/**
 * P57-B — Rendu d'un menu groupé.
 *
 * Partagé entre la sidebar desktop et le drawer mobile : une seule source de
 * vérité pour l'ordre, le groupement et le filtrage RBAC (avant, les deux
 * rendus dupliquaient la liste et pouvaient diverger).
 *
 * `onNavigate` est appelé à chaque clic (le drawer mobile l'utilise pour se
 * refermer).
 */
function NavMenu({
  groups,
  onNavigate,
  t,
}: {
  groups: { groupKey: string; items: NavItem[] }[];
  onNavigate?: () => void;
  t: (key: string) => string;
}) {
  return (
    <>
      {groups.map((group) => (
        <div key={group.groupKey} className="mb-1">
          <div
            data-testid="nav-group-label"
            className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
          >
            {t(group.groupKey)}
          </div>
          <div className="space-y-1">
            {group.items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-accent hover:text-accent-foreground"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  {t(item.labelKey)}
                </NavLink>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const [dark, setDark] = useState(true);
  const [locale, setLocaleState] = useState<Locale>(getLocale());

  const switchLocale = () => {
    const next: Locale = locale === "fr" ? "en" : "fr";
    setLocale(next);
    setLocaleState(next);
  };

  useEffect(() => {
    // Tailwind v4 dark mode
    document.documentElement.classList.toggle("dark", dark);
    document.body.classList.toggle("dark", dark);
    document.documentElement.dataset.theme = dark ? "dark" : "light";
  }, [dark]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isRespo = useCan("admin");
  // Un groupe sans entrée visible n'est pas rendu du tout (évite un titre
  // de groupe vide pour un technicien qui n'a que 2 entrées visibles).
  const visibleGroups = NAV_GROUPS.map((g) => ({
    groupKey: g.groupKey,
    items: g.items.filter((item) => !item.respoOnly || isRespo),
  })).filter((g) => g.items.length > 0);

  // Burger menu mobile (D103 responsive)
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Close drawer on route change (handler attached to NavLink via render-prop)
  // We just use state to track.

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* ═══ Sidebar : DESKTOP (lg+) ═══ */}
      <aside className="hidden lg:flex w-60 border-r border-border bg-card flex-col">
        {/*
          P58-A : hauteur d'en-tête FIXE et partagée avec le header principal
          (ligne 295). Avant, `py-4` ici vs `py-3` là donnait 57px vs 49px :
          les deux border-b ne se rejoignaient pas au coin haut-gauche
          (marche de 8px). Une hauteur explicite garantit la jonction quelle
          que soit la taille de police ou le mode.
        */}
        <div className="flex h-14 items-center gap-2 border-b border-border px-4">
          <Wrench className="h-5 w-5 text-primary" />
          <span className="font-semibold">{ht("POG Maintenance")}</span>
        </div>

        <nav className="flex-1 overflow-y-auto p-2">
          <NavMenu groups={visibleGroups} t={t} />
        </nav>

        <div className="border-t border-border p-3 space-y-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={() => setDark(!dark)}
          >
            {dark ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
            {dark ? t("nav.light_mode") : t("nav.dark_mode")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={switchLocale}
            data-testid="locale-switch"
            title={locale === "fr" ? "Switch to English" : "Passer en français"}
          >
            <span className="mr-2 text-xs font-mono">🌐</span>
            {locale.toUpperCase()}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            {t("nav.logout")}
          </Button>
        </div>
      </aside>

      {/* ═══ Drawer mobile/tablette (<lg) ═══ */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setDrawerOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-card border-r border-border flex flex-col transform transition-transform duration-200 ease-in-out lg:hidden ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar-drawer"
      >
        <div className="border-b border-border px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-primary" />
            <span className="font-semibold">{ht("POG Maintenance")}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setDrawerOpen(false)} aria-label="Close menu">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <nav className="flex-1 overflow-y-auto p-2">
          <NavMenu
            groups={visibleGroups}
            t={t}
            onNavigate={() => setDrawerOpen(false)}
          />
        </nav>
        <div className="border-t border-border p-3 space-y-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={() => setDark(!dark)}
          >
            {dark ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
            {dark ? t("nav.light_mode") : t("nav.dark_mode")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={switchLocale}
          >
            <span className="mr-2 text-xs font-mono">🌐</span>
            {locale.toUpperCase()}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            {t("nav.logout")}
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-hidden">
        {/*
          h-14 : doit rester SYNCHRONE avec le header du sidebar (ligne 190).
          Ne pas passer à py-* ici sans mettre à jour l'autre.
        */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-2 border-b border-border bg-card px-4 sm:px-6">
          <div className="flex items-center gap-2 min-w-0">
            {/* Burger menu mobile */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden"
              data-testid="burger-menu"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <h2 className="text-sm sm:text-base font-semibold truncate">{ht("Module Maintenance")}</h2>
          </div>
          <div className="text-xs sm:text-sm text-muted-foreground truncate">
            <span className="hidden sm:inline">{user?.name} ({user?.role})</span>
            <span className="sm:hidden">{user?.name}</span>
          </div>
        </header>
        <div className="p-4 sm:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}